import { useEffect, useState } from "react";
import { http, rp, rpShort, nf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Stat, Panel } from "@/components/Ui";
import AiInsight from "@/components/AiInsight";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  AreaChart, Area, PieChart, Pie, Cell,
} from "recharts";

const COLORS = ["#4338CA", "#8B5CF6", "#16A34A", "#F59E0B", "#FF3B30", "#0EA5E9"];

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);

  useEffect(() => {
    http.get("/dashboard").then((r) => setD(r.data)).catch(() => setD(false));
  }, []);

  if (!d)
    return (
      <div className="p-8 mono text-sm text-[#525252]" data-testid="dashboard-loading">
        Memuat dashboard…
      </div>
    );

  const showCost = user.role === "keuangan" || user.role === "purchasing";

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        subtitle={user.branch ? `Kantor Cabang ${user.branch}` : "Kantor Pusat"}
        title="Dashboard Eksekutif"
        testId="dashboard-header"
      />

      <div className="p-6 sm:p-8 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat testId="stat-stock-qty" label="Total Qty Stock" value={nf(d.total_stock_qty)} sub="unit tersedia" />
          <Stat testId="stat-stock-nominal" label="Nilai Stock (Harga Jual A)" value={rpShort(d.total_stock_nominal)} sub={rp(d.total_stock_nominal)} accent="#4338CA" />
          <Stat testId="stat-sales-qty" label="Total Qty Penjualan" value={nf(d.total_sales_qty)} sub="akumulasi periode" />
          <Stat testId="stat-sales-nominal" label="Nilai Penjualan" value={rpShort(d.total_sales_nominal)} sub={rp(d.total_sales_nominal)} accent="#16A34A" />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-4">
          <Stat testId="stat-produk" label="Jumlah Produk" value={nf(d.total_produk)} />
          <Stat testId="stat-eta" label="Shipment ETA Aktif" value={nf(d.total_eta_shipment)} />
          <Stat testId="stat-proyeksi" label="Usulan Proyeksi" value={nf(d.total_proyeksi)} />
          {showCost ? (
            <Stat testId="stat-import" label="Nilai Import Berjalan" value={rpShort(d.total_nilai_import || 0)} accent="#FF3B30" />
          ) : (
            <Stat testId="stat-lokasi" label="Lokasi Terpantau" value={nf(d.stock_per_lokasi.length)} />
          )}
        </div>

        {showCost && d.avg_margin && (
          <div className="grid grid-cols-3 gap-4">
            <Stat testId="stat-margin-a" label="Rata-rata Margin Kat. A" value={`${d.avg_margin.a}%`} />
            <Stat testId="stat-margin-b" label="Rata-rata Margin Kat. B" value={`${d.avg_margin.b}%`} />
            <Stat testId="stat-margin-c" label="Rata-rata Margin Kat. C" value={`${d.avg_margin.c}%`} />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <Panel testId="chart-trend" title="Trend Penjualan 12 Bulan Terakhir" className="lg:col-span-8">
            <div className="h-[280px] p-3">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={d.trend_penjualan}>
                  <defs>
                    <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#4338CA" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#4338CA" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#eee" vertical={false} />
                  <XAxis dataKey="periode" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 10 }} width={55} />
                  <Tooltip formatter={(v) => nf(v)} />
                  <Area type="monotone" dataKey="qty" stroke="#4338CA" strokeWidth={2} fill="url(#g1)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel testId="chart-kategori" title="Distribusi Kategori Pemasaran" className="lg:col-span-4">
            <div className="h-[280px] p-3">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={d.kategori_distribusi} dataKey="qty" nameKey="kategori" innerRadius={50} outerRadius={85}>
                    {d.kategori_distribusi.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => nf(v)} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1 -mt-2">
                {d.kategori_distribusi.map((k, i) => (
                  <div key={k.kategori} className="flex items-center gap-2 text-[11px]">
                    <span className="h-2 w-2" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="flex-1 truncate">{k.kategori}</span>
                    <span className="mono">{nf(k.qty)}</span>
                  </div>
                ))}
              </div>
            </div>
          </Panel>

          <Panel testId="chart-stock-lokasi" title="Stock per Gudang & Cabang" className="lg:col-span-6">
            <div className="h-[260px] p-3">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.stock_per_lokasi}>
                  <CartesianGrid stroke="#eee" vertical={false} />
                  <XAxis dataKey="lokasi" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={60} interval={0} />
                  <YAxis tick={{ fontSize: 10 }} width={55} />
                  <Tooltip formatter={(v) => nf(v)} />
                  <Bar dataKey="qty" fill="#0a0a0a" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel testId="chart-sales-cabang" title="Penjualan per Cabang" className="lg:col-span-6">
            <div className="h-[260px] p-3">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.penjualan_per_cabang}>
                  <CartesianGrid stroke="#eee" vertical={false} />
                  <XAxis dataKey="cabang" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={60} interval={0} />
                  <YAxis tick={{ fontSize: 10 }} width={55} />
                  <Tooltip formatter={(v) => nf(v)} />
                  <Bar dataKey="qty" fill="#4338CA" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel testId="panel-top-produk" title="Produk Terlaris" className="lg:col-span-6">
            <div className="overflow-auto max-h-[300px]">
              <table className="erp-table">
                <thead>
                  <tr><th>Kode</th><th>Deskripsi</th><th className="num">Qty</th><th className="num">Nominal</th></tr>
                </thead>
                <tbody>
                  {d.top_produk.map((p) => (
                    <tr key={p.kode_stock}>
                      <td className="mono">{p.kode_stock}</td>
                      <td className="max-w-[220px] truncate">{p.deskripsi}</td>
                      <td className="num">{nf(p.qty)}</td>
                      <td className="num">{rpShort(p.nominal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel testId="panel-eta" title="ETA Kedatangan Terdekat" className="lg:col-span-6">
            <div className="overflow-auto max-h-[300px]">
              <table className="erp-table">
                <thead>
                  <tr><th>Kode</th><th>Supplier</th><th>Tujuan</th><th className="num">Qty</th><th>ETA</th></tr>
                </thead>
                <tbody>
                  {d.eta_terdekat.map((e, i) => (
                    <tr key={i}>
                      <td className="mono">{e.kode_stock}</td>
                      <td>{e.supplier}</td>
                      <td>{e.tujuan}</td>
                      <td className="num">{nf(e.qty)}</td>
                      <td className="mono">{e.eta}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <div className="lg:col-span-12">
            <AiInsight scope="dashboard" />
          </div>
        </div>
      </div>
    </div>
  );
}
